import SearchBar from "components/BaseWidgets/search";
import Tabs from "components/BaseWidgets/tab";
import "./style.scss";
import JewelCard from "components/Cards/JewelCard";
import WeddingStoreBannerCard from "components/WeddingStore/components/BannerCard";
import Select from "components/BaseWidgets/select";
import { useEffect, useState } from "react";
import EmptyState from "components/BaseWidgets/emptyState";
import {
  useStoreCategory,
  useStoreLocations,
  useWeddingStore,
} from "hooks/api/useWedding";
import Skeleton from "components/BaseWidgets/skeleton";

function useDebounce(value, delay = 500) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export default function WeddingStore() {
  const [search, setSearch] = useState("");
  const [selectedLocation, setSelectedLocation] = useState();
  const debouncedSearch = useDebounce(search, 500);
  const [selectOptions, setSelectOptions] = useState([]);
  const [expandedId, setExpandedId] = useState(null);

  const { data: locationLists, isLoading: isLocationLoading } =
    useStoreLocations();
  const { data: categoryList, isLoading: isCategoryLoading } =
    useStoreCategory();

  const [activeTab, setActiveTab] = useState("0");
  useEffect(() => {
    if (categoryList?.store_category.length) {
      setActiveTab(String(categoryList?.store_category[0].id));
    }
  }, [categoryList]);

  // listing api
  const { data: listData, isLoading: isListLoading } = useWeddingStore(
    {
      category: activeTab,
      location: selectedLocation,
      search: debouncedSearch,
      see_all: true
    },
    {
      enabled: !!activeTab && selectedLocation !== undefined,
    }
  );

  useEffect(() => {
    if (Array.isArray(locationLists) && locationLists.length > 0) {
      const formattedLocations = locationLists.map((loc) => ({
        value: loc.id,
        label: loc.is_all
          ? loc.district
          : `${loc.district}, ${loc.state}`,
      }));

      setSelectOptions(formattedLocations);
      if (!selectedLocation) {
        setSelectedLocation(formattedLocations[0].value);
      }
    }
  }, [locationLists]);

  const TabContent = ({ data = [], isLoading }) => {
    return (
      <div
        className={`blog-list ${!data || data.length === 0 ? "empty-blog" : ""}`}
      >
        {isLoading ? (
          Array.from({ length: 3 }).map((_, idx) => (
            <JewelCard key={idx} isLoading={true} />
          ))
        ) : data?.length > 0 ? (
          data.map((blog) => (
            <JewelCard
              key={blog.id || blog.index}
              blog={blog}
              isExpanded={expandedId === blog.id}
              onToggle={() =>
                setExpandedId(expandedId === blog.id ? null : blog.id)
              }
            />
          ))
        ) : (
          <EmptyState />
        )}
      </div>
    );
  };

  const tabItems = categoryList?.store_category?.map((cat) => ({
    key: String(cat.id),
    label: cat.name,
    children: (
      <TabContent
        data={listData}
        isLoading={isLocationLoading || isListLoading}
      />
    ),
  }));

  return (
    <div className="container store-container">
      <WeddingStoreBannerCard
        showIndicators={true}
        showArrows={true}
        autoSlide={true}
        slideInterval={4000}
      />
      <div className="search-header">
        <div className="location-wrapper font-title-xs fw-600">
          <span className="text">Viewing stores in</span>
          <Select
            label={false}
            isLoading={isLocationLoading}
            SkeletonHeight={17}
            value={selectedLocation}
            options={selectOptions}
            className="location-dropdown"
            inputClassName=""
            popupClassName="location-popup"
            placeholder="Select location"
            widthVariant="customWidth"
            customWidthClass="custom-location-width"
            onChange={(value) => setSelectedLocation(value)}
            showSearch={false}
          />
        </div>
        <SearchBar onSearch={(value) => setSearch(value)} />
      </div>
      {isCategoryLoading ? (
        <Skeleton label={false} height={300} />
      ) : (
        <Tabs
          activeKey={activeTab}
          onChange={(key) => setActiveTab(key)}
          variant="secondary"
          items={tabItems}
        />
      )}
    </div>
  );
}
